import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppContext } from "../../../../AppContext";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { IMyReservationsViewModelWrapper } from "../models/MyReservationsViewModelWrapper";

export const useMyReservations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedStudio, isDesktopOrTablet } = useAppContext();
  const [reservationToShowId, setReservationToShowId] = useState<
    number | undefined
  >(undefined);
  const [musicDialogEventId, setMusicDialogEventId] = useState<
    number | undefined
  >(undefined);
  const { mutate } = useApiMutation();

  const urlReservationId = searchParams.get("urlReservationId");

  const {
    data: userEvents,
    isLoading,
    refetch,
  } = useApiQuery<IMyReservationsViewModelWrapper>({
    queryKey: [endpointsMetadata.myReservations.getAll],
    endpoint: endpointsMetadata.myReservations.getAll,
    enabled: selectedStudio && selectedStudio?.id > 0,
    disableErrorToast: false,
    retry: false,
    globalIsLoading: false,
  });

  // Handle URL parameters for reservation dialog
  useEffect(() => {
    if (!urlReservationId || isLoading) {
      return;
    }

    const urlEventParsed = Number(urlReservationId);
    const isAvailable = userEvents?.allEvents.some(
      (x) => x.reservationId === urlEventParsed
    );

    if (isAvailable) {
      setReservationToShowId(urlEventParsed);
    }
  }, [isLoading, urlReservationId, userEvents?.allEvents]);

  // Sync URL parameters with dialog state
  useEffect(() => {
    if (!isLoading) {
      const newParams = new URLSearchParams(searchParams);

      if (!!reservationToShowId) {
        newParams.set("urlReservationId", reservationToShowId.toString());
      } else {
        newParams.delete("urlReservationId");
      }

      // replace so opening/closing the reservation dialog doesn't add history.
      setSearchParams(newParams, { replace: true });
    }
  }, [reservationToShowId, isLoading, searchParams, setSearchParams]);

  const onEventReservationCancel = useCallback(
    async (reservationId?: number) => {
      let id = reservationToShowId;
      setReservationToShowId(undefined);
      if (!reservationId) {
        return;
      }

      mutate(
        {
          endpoint: endpointsMetadata.myReservations.cancelReservation,
          globalIsLoading: true,
          method: "post",
          invalidateKeys: [
            [endpointsMetadata.myReservations.getAll],
            [endpointsMetadata.portalCalendar.getAll],
          ],
          ...{ reservationId },
        },
        {
          onSuccess: async () => {
            // The invalidation above has already started the reload: wait for it instead
            // of cancelling it and loading the whole history a second time.
            await refetch({ cancelRefetch: false });
            setReservationToShowId(id);
          },
        }
      );
    },
    [reservationToShowId, mutate, refetch]
  );

  const onTryEventReservationCancel = useCallback(
    (reservationId?: number) => {
      showCustomConfirmDialog(
        "Сигурни ли сте, че искате да отмените резервацията си?",
        async () => await onEventReservationCancel(reservationId),
        () => {},
        "warning",
        "Да",
        "Не"
      );
    },
    [onEventReservationCancel]
  );

  const handleOpenMusicDialog = useCallback((eventId: number) => {
    setMusicDialogEventId(eventId);
  }, []);

  const handleCloseMusicDialog = useCallback(() => {
    setMusicDialogEventId(undefined);
  }, []);

  const handleCloseReservationDialog = useCallback(() => {
    setReservationToShowId(undefined);
  }, []);

  const handleOpenReservationDialog = useCallback((reservationId: number) => {
    setReservationToShowId(reservationId);
  }, []);

  // Determine if user has reservation for the music dialog event
  const hasReservationForMusicEvent = musicDialogEventId
    ? userEvents?.allEvents.some(
        (x) => x.eventDetails.id === musicDialogEventId && !x.isCancelled
      )
    : false;

  return {
    // Data
    userEvents,
    isLoading,
    isDesktopOrTablet,

    // Dialog state
    reservationToShowId,
    musicDialogEventId,
    hasReservationForMusicEvent,

    // Handlers
    onTryEventReservationCancel,
    onEventReservationCancel,
    handleOpenMusicDialog,
    handleCloseMusicDialog,
    handleCloseReservationDialog,
    handleOpenReservationDialog,
  };
};
